import { findIconDefinition, library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ArrowBackIosNew,
  ArrowForwardIos,
  CalendarTodayOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  ContentCopyOutlined,
  LocationOnOutlined,
  ShareOutlined,
  WhatsApp
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Snackbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { useMicrospecDynamicUIContext } from '../../MicrospecDynamicUI/GlobalProvider';
import {
  getDataFromCollection,
  getDocById,
  numberWithCommas,
} from '../../MicrospecDynamicUI/Helpers/dataUtils';
import ContactForm from '../../MicrospecDynamicUI/Pages/SectionsMapper/Contact/ContactForm';
import PagesLayout from '../components/PagesLayout';

// Register all solid FA icons once
library.add(fas);

/**
 * Resolve a Font Awesome icon name string (e.g. "fa-bed", "bed", "faBed")
 * to a valid icon definition. Returns null if not found.
 */
const resolveFA = (iconName) => {
  if (!iconName || typeof iconName !== 'string') return null;
  // Normalise: strip "fa-" / "fas-" prefix, convert camelCase to kebab
  const normalised = iconName
    .replace(/^fa[sr]?-/, '')
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .toLowerCase();
  const def = findIconDefinition({ prefix: 'fas', iconName: normalised });
  return def || null;
};

// ─── Image Carousel ──────────────────────────────────────────────────────────
const ImageCarousel = ({ images }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          height: isMobile ? 260 : 480,
          bgcolor: 'grey.200',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography color="text.secondary">لا توجد صور</Typography>
      </Box>
    );
  }

  const goTo = (index) => {
    setCurrentIndex((index + images.length) % images.length);
  };

  return (
    <Box sx={{ position: 'relative', borderRadius: 4, overflow: 'hidden' }}>
      <AnimatePresence mode="wait">
        <motion.img
          key={currentIndex}
          src={images[currentIndex]}
          alt={`صورة ${currentIndex + 1}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            width: '100%',
            height: isMobile ? 260 : 480,
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </AnimatePresence>

      {images.length > 1 && (
        <>
          <IconButton
            onClick={() => goTo(currentIndex + 1)}
            sx={{
              position: 'absolute',
              top: '50%',
              left: 12,
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.85)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
              boxShadow: 2,
            }}
          >
            <ArrowBackIosNew sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            onClick={() => goTo(currentIndex - 1)}
            sx={{
              position: 'absolute',
              top: '50%',
              right: 12,
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.85)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
              boxShadow: 2,
            }}
          >
            <ArrowForwardIos sx={{ fontSize: 18 }} />
          </IconButton>

          {/* Dots */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
            }}
          >
            {images.map((_, i) => (
              <Box
                key={i}
                onClick={() => goTo(i)}
                sx={{
                  width: i === currentIndex ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: i === currentIndex ? '#fff' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </Box>

          {/* Counter */}
          <Chip
            label={`${currentIndex + 1} / ${images.length}`}
            size="small"
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: '#fff',
              fontWeight: 600,
            }}
          />
        </>
      )}
    </Box>
  );
};

// ─── Feature Item ────────────────────────────────────────────────────────────
const FeatureItem = ({ feature, value }) => {
  const isBoolean = feature.type === 'boolean';
  const faDef = resolveFA(feature.icon);

  // For boolean features: if value doesn't exist (null/undefined), don't render
  if (isBoolean && value == null) return null;

  if (isBoolean) {
    return (
      <Chip
        icon={
          value ? (
            <CheckCircleOutlined sx={{ fontSize: 18, color: 'success.main' }} />
          ) : (
            <CloseOutlined sx={{ fontSize: 18, color: 'error.main' }} />
          )
        }
        label={feature.name}
        variant="outlined"
        sx={{
          fontWeight: 600,
          fontSize: '0.85rem',
          py: 2.5,
          pl: 1.5,
          pr: 1.5,
          borderRadius: 2,
          borderColor: 'divider',
          '& .MuiChip-icon': { ml: 0.5 },
          '& .MuiChip-label': { px: 1 },
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        bgcolor: 'action.hover',
      }}
    >
      <Box sx={{ fontSize: 18, color: 'primary.main', minWidth: 24, textAlign: 'center' }}>
        {faDef ? (
          <FontAwesomeIcon icon={faDef} />
        ) : (
          <CheckCircleOutlined sx={{ fontSize: 18 }} />
        )}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          {feature.name}
        </Typography>
        <Typography variant="body1" fontWeight={600}>
          {`${value}${feature.suffix ? ' ' + feature.suffix : ''}`}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Map Placeholder ─────────────────────────────────────────────────────────
const LocationMap = ({ coordinates, address }) => {
  const mapUrl = coordinates
    ? `https://maps.google.com/maps?q=${coordinates.lat},${coordinates.lng}&z=15&output=embed`
    : null;

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocationOnOutlined color="primary" />
        <Typography variant="subtitle1" fontWeight={600}>
          الموقع
        </Typography>
      </Box>
      {address && (
        <Box sx={{ px: 2.5, pb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {address}
          </Typography>
        </Box>
      )}
      {mapUrl && (
        <Box sx={{ position: 'relative', height: 280, bgcolor: 'grey.100' }}>
          <iframe
            title="موقع العقار"
            src={mapUrl}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Box>
      )}
    </Paper>
  );
};

// ─── Owner Contact Card ──────────────────────────────────────────────────────
const OwnerContactCard = ({ owner }) => {
  const navigate = useNavigate();

  if (!owner) return null;

  const fullName = `${owner.first_name || ''} ${owner.last_name || ''}`.trim();

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, p: 3, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        معلومات المعلن
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Avatar
          src={owner.photo_url}
          alt={fullName}
          sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}
        >
          {fullName.charAt(0)}
        </Avatar>
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>
            {fullName}
          </Typography>
          {owner.address && (
            <Typography variant="body2" color="text.secondary">
              {owner.address}
            </Typography>
          )}
        </Box>
      </Box>
      <Button
        variant="outlined"
        fullWidth
        onClick={() => navigate(`/owner/${owner.id || owner.uid}`)}
        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
      >
        عرض الملف الشخصي
      </Button>
    </Paper>
  );
};

// ─── Property Public Page ────────────────────────────────────────────────────
const PropertyPublicPage = () => {
  const { propertyId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { firestore } = cloudProvider;

  const [property, setProperty] = useState(null);
  const [propertyLoading, setPropertyLoading] = useState(true);

  const [owner, setOwner] = useState(null);
  const [ownerLoading, setOwnerLoading] = useState(true);

  const [category, setCategory] = useState(null);

  const [allFeatures, setAllFeatures] = useState([]);
  const [, setFeaturesLoading] = useState(true);
  const [snackOpen, setSnackOpen] = useState(false);

  // Load property
  useEffect(() => {
    if (!firestore?.instance || !propertyId) return;
    setPropertyLoading(true);
    getDocById(
      setProperty,
      (retrieved) => { if (retrieved) setPropertyLoading(false); },
      firestore.instance,
      'properties',
      propertyId,
      false
    );
  }, [firestore?.instance, propertyId]);

  // Load owner once property is available
  useEffect(() => {
    if (!firestore?.instance || !property?.owner_id) return;
    setOwnerLoading(true);
    getDocById(
      setOwner,
      (retrieved) => { if (retrieved) setOwnerLoading(false); },
      firestore.instance,
      'users',
      property.owner_id,
      true
    );
  }, [firestore?.instance, property?.owner_id]);

  // Load category
  useEffect(() => {
    if (!firestore?.instance || !property?.category_id) return;
    getDocById(setCategory, null, firestore.instance, 'categories', property.category_id, true);
  }, [firestore?.instance, property?.category_id]);

  // Load features
  useEffect(() => {
    if (!firestore?.instance) return;
    getDataFromCollection(setAllFeatures, (r) => { if (r) setFeaturesLoading(false); }, firestore.instance, 'features', true, 100);
  }, [firestore?.instance]);

  // Resolve features for this property
  const propertyFeatures = useMemo(() => {
    if (!property?.features || allFeatures.length === 0) return [];
    return Object.entries(property.features)
      .map(([featureId, value]) => {
        const feature = allFeatures.find((f) => f.id === featureId);
        if (!feature) return null;
        return { feature, value };
      })
      .filter(Boolean);
  }, [property?.features, allFeatures]);

  const nonBooleanFeatures = propertyFeatures.filter(({ feature }) => feature.type !== 'boolean');
  const booleanFeatures = propertyFeatures.filter(({ feature }) => feature.type === 'boolean');

  // Contact form spec
  const contactFormSpec = useMemo(() => {
    if (!property) return null;
    return {
      id: `leave-message-${property?.id}`,
      sectionTitle: { localizationObject: { type: "Literal", text: "" } },
      collectionToStoreTo: `properties/${property?.id}/unprocessed_messages`,
      sectionBackgroundColor: "transparent",
      fields: [
        {
          key: "senderFirstName",
          type: "text",
          label: { localizationObject: { type: "Literal", text: "الاسم الأول" } },
          required: true,
        },
        {
          key: "senderLastName",
          type: "text",
          label: { localizationObject: { type: "Literal", text: "اسم العائلة" } },
          required: false,
        },
        {
          key: "senderPhone",
          type: "text",
          label: { localizationObject: { type: "Literal", text: "رقم الهاتف" } },
          required: true,
        },
        {
          key: "propertyTitle",
          type: "text",
          label: { localizationObject: { type: "Literal", text: "عنوان العقار" } },
          defaultValue: property?.title,
          disabled: true,
        },
        {
          key: "propertyId",
          type: "text",
          label: { localizationObject: { type: "Literal", text: "معرف العقار" } },
          defaultValue: property?.id,
          disabled: true,
          hidden: true,
        },
        {
          key: "message",
          type: "text",
          label: { localizationObject: { type: "Literal", text: "الرسالة" } },
          required: false,
          multiline: true,
        },
      ],
      submitButtonText: { localizationObject: { type: "Literal", text: "إرسال الرسالة" } },
      clearButtonText: { localizationObject: { type: "Literal", text: "مسح" } },
      successMessage: { localizationObject: { type: "Literal", text: "تم إرسال رسالتك بنجاح، سيتواصل معك المالك قريباً." } },
      rootSx: {
        py: 0,
        px: 0,
      },
    };
  }, [property]);

  const formattedDate = property?.updated_at
    ? new Date(
        property.updated_at?.seconds
          ? property.updated_at.seconds * 1000
          : property.updated_at
      ).toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  // Loading state
  if (propertyLoading) {
    return (
      <PagesLayout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Skeleton variant="rounded" height={480} sx={{ borderRadius: 4, mb: 3 }} />
          <Skeleton variant="text" width="60%" height={40} />
          <Skeleton variant="text" width="40%" height={24} sx={{ mt: 1 }} />
          <Skeleton variant="rounded" height={200} sx={{ mt: 3, borderRadius: 3 }} />
        </Container>
      </PagesLayout>
    );
  }

  // Not found
  if (!property || property.active !== true || property.approval_status !== 'approved') {
    return (
      <PagesLayout>
        <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            العقار غير موجود
          </Typography>
          <Typography color="text.secondary">
            قد يكون العقار قد أزيل أو أن الرابط غير صحيح
          </Typography>
        </Container>
      </PagesLayout>
    );
  }

  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  const seoTitle = property.title
    ? `${property.title} | عقاركو`
    : 'عقاركو - منصة العقارات';
  const seoDescription = property.description
    ? property.description.slice(0, 160)
    : `${property.title || 'عقار'} - ${category?.name || ''} ${property.address_display_name || property.address || ''}`.trim();
  const seoImage = property.images?.[0] || '';

  return (
    <PagesLayout>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={pageUrl} />
        {seoImage && <meta property="og:image" content={seoImage} />}
        <meta property="og:locale" content="ar_SA" />
        <meta property="og:site_name" content="عقاركو" />

        {/* Twitter Card */}
        <meta name="twitter:card" content={seoImage ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        {seoImage && <meta name="twitter:image" content={seoImage} />}
      </Helmet>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Title & Category */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
            {category && (
              <Chip
                label={category.name}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            )}
            {formattedDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                <CalendarTodayOutlined sx={{ fontSize: 16 }} />
                <Typography variant="body2">{formattedDate}</Typography>
              </Box>
            )}
          </Box>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={800}>
            {property.title}
          </Typography>
          {(property.address_display_name || property.address) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, color: 'text.secondary' }}>
              <LocationOnOutlined sx={{ fontSize: 18 }} />
              <Typography variant="body1">
                {property.address_display_name || property.address}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Image Carousel */}
        <ImageCarousel images={property.images} />

        {/* Share Buttons */}
        {(() => {
          const priceText = property.price_on_request
            ? 'السعر عند الطلب'
            : `${numberWithCommas(property.price)} ₪`;
          const locationText = property.address_display_name || property.address || '';
          const descSnippet = property.description
            ? property.description.length > 100
              ? property.description.slice(0, 100) + '...'
              : property.description
            : '';
          const pageUrl = window.location.href;

          const whatsappText = [
            `العنوان: *${property.title}*`,
            '',
            `السعر: ${priceText}`,
            locationText ? `الموقع: ${locationText}` : '',
            category ? `التصنيف: ${category.name}` : '',
            descSnippet ? `\nالوصف: ${descSnippet}` : '',
            '',
            `الرابط: ${pageUrl}`,
          ].filter(Boolean).join('\n');

          const copyText = [
            `العنوان: ${property.title}`,
            `السعر: ${priceText}`,
            locationText ? `الموقع: ${locationText}` : '',
            category ? `التصنيف: ${category.name}` : '',
            descSnippet ? `\nالوصف: ${descSnippet}` : '',
            '',
            `الرابط: ${pageUrl}`,
          ].filter(Boolean).join('\n');

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <ShareOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mr: 0.5 }}>
                مشاركة
              </Typography>
              <Tooltip title="مشاركة عبر واتساب">
                <IconButton
                  size="small"
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(whatsappText)}`, '_blank')}
                  sx={{ bgcolor: '#25D366', color: '#fff', '&:hover': { bgcolor: '#1DA851' }, width: 36, height: 36 }}
                >
                  <WhatsApp sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="نسخ التفاصيل">
                <IconButton
                  size="small"
                  onClick={() => { navigator.clipboard.writeText(copyText); setSnackOpen(true); }}
                  sx={{ bgcolor: 'action.hover', width: 36, height: 36 }}
                >
                  <ContentCopyOutlined sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })()}

        {/* Content Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            gap: 3,
            mt: 4,
          }}
        >
          {/* Left Column */}
          <Box>
            {/* Price */}
            <Paper elevation={0} sx={{ borderRadius: 3, p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                السعر
              </Typography>
              <Typography variant="h4" fontWeight={800} color="primary.main">
                {property.price_on_request
                  ? 'السعر عند الطلب'
                  : `${numberWithCommas(property.price)} ₪`}
              </Typography>
            </Paper>

            {/* Description */}
            {property.description && (
              <Paper elevation={0} sx={{ borderRadius: 3, p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  الوصف
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}
                >
                  {property.description}
                </Typography>
              </Paper>
            )}

            {/* Features (Non-boolean) */}
            {nonBooleanFeatures.length > 0 && (
              <Paper elevation={0} sx={{ borderRadius: 3, p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  المميزات
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 1.5,
                    mt: 1,
                  }}
                >
                  {nonBooleanFeatures.map(({ feature, value }) => (
                    <FeatureItem key={feature.id} feature={feature} value={value} />
                  ))}
                </Box>
              </Paper>
            )}

            {/* Additional Features (Boolean) */}
            {booleanFeatures.length > 0 && (
              <Paper elevation={0} sx={{ borderRadius: 3, p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  مميزات إضافية
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                  {booleanFeatures.map(({ feature, value }) => (
                    <FeatureItem key={feature.id} feature={feature} value={value} />
                  ))}
                </Box>
              </Paper>
            )}

            {/* Location */}
            {(property.address || property.coordinates) && (
              <Box sx={{ mb: 3 }}>
                <LocationMap coordinates={property.coordinates} address={property.address} />
              </Box>
            )}

          </Box>

          {/* Right Column (Sidebar) */}
          <Box>
            <Box sx={{ position: 'sticky', top: 90 }}>
              {/* Owner Card */}
              {ownerLoading ? (
                <Paper elevation={0} sx={{ borderRadius: 3, p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                  <Skeleton variant="circular" width={56} height={56} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                </Paper>
              ) : (
                <Box sx={{ mb: 3 }}>
                  <OwnerContactCard owner={owner} />
                </Box>
              )}

              {/* Quick Info */}
              <Paper elevation={0} sx={{ borderRadius: 3, p: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  ملخص العقار
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {category && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">الفئة</Typography>
                      <Typography variant="body2" fontWeight={600}>{category.name}</Typography>
                    </Box>
                  )}
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">السعر</Typography>
                    <Typography variant="body2" fontWeight={600} color="primary.main">
                      {property.price_on_request
                        ? 'عند الطلب'
                        : `${numberWithCommas(property.price)} ₪`}
                    </Typography>
                  </Box>
                  {formattedDate && (
                    <>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">تاريخ النشر</Typography>
                        <Typography variant="body2" fontWeight={600}>{formattedDate}</Typography>
                      </Box>
                    </>
                  )}
                  {(property.address_display_name || property.address) && (
                    <>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">الموقع</Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ maxWidth: 160, textAlign: 'left' }} noWrap>
                          {property.address_display_name || property.address}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Paper>

              {/* Contact Form */}
              {contactFormSpec && (
                <Paper elevation={0} sx={{ borderRadius: 3, p: 3, mt: 3, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    أرسل رسالة للمعلن
                  </Typography>
                  <ContactForm spec={contactFormSpec} />
                </Paper>
              )}
            </Box>
          </Box>
        </Box>
      </Container>

      <Snackbar
        open={snackOpen}
        autoHideDuration={2000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled">
          تم نسخ الرابط بنجاح
        </Alert>
      </Snackbar>
    </PagesLayout>
  );
};

export default PropertyPublicPage;
